/** Selected relationship metadata only; bounded observation never starts collection. */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
import {
  projectSourceStatusSchema,
  shopifyIntegrationPropertiesSchema,
  type StoreProjectSource,
} from '@testfeed/integration-contracts'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { shopifyApiClient } from '../../shopifyApiClient'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
import { integrationResponse } from './useIntegrationsPage'
import { collectionLabel, isCollectionPending, shouldPollIntegration } from './integration-page-model'
const copy = shopifyComponentCopy.integrationsPage

export function IntegrationSourceDetails(props: {
  source: StoreProjectSource
  shopify: ShopifyTokenSource
  canUnlink: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const scope = useShopifyAppContext()
  const started = useRef(0)
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible')
  const [confirming, setConfirming] = useState(false)
  useEffect(() => {
    started.current = Date.now()
    const changed = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', changed)
    return () => document.removeEventListener('visibilitychange', changed)
  }, [])
  const status = useQuery({
    enabled: visible,
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, [
      'integration-status',
      props.source.projectId,
      props.source.relationshipId,
    ]),
    queryFn: async ({ signal }) => {
      const result = projectSourceStatusSchema.parse(
        await integrationResponse(
          await shopifyApiClient(props.shopify).api.integrations.status.$post(
            { json: { projectId: props.source.projectId, relationshipId: props.source.relationshipId } },
            { init: { signal } },
          ),
        ),
      )
      if (result.projectId !== props.source.projectId || result.relationshipId !== props.source.relationshipId)
        throw new Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
      return result
    },
    retry: false,
    ...shopifyQueryPolicies.integrations,
    refetchInterval: (query) => {
      const pending = !!query.state.data && isCollectionPending(query.state.data)
      const poll = shouldPollIntegration({
        visible,
        failed: !!query.state.error,
        pending,
        elapsed: Date.now() - started.current,
      })
      if (poll) return 30_000
      return false
    },
    refetchIntervalInBackground: false,
  })
  const unlink = useShopifyTrackedMutation(props.shopify, 'unlink-source', {
    mutationFn: async () => {
      const properties = shopifyIntegrationPropertiesSchema.parse(
        await integrationResponse(await shopifyApiClient(props.shopify).api.integrations.properties.$get()),
      )
      if (properties.organizationId !== scope.orgId || properties.storeConnectionId !== scope.connectionId)
        throw new Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
      const relationship = properties.relationships.find((row) => row.id === props.source.relationshipId)
      if (!relationship) {
        props.onSaved()
        return
      }
      await integrationResponse(
        await shopifyApiClient(props.shopify).api.integrations.properties.$post({
          json: {
            operation: 'remove-link',
            projectId: props.source.projectId,
            relationshipId: relationship.id,
            expectedRevision: relationship.revision,
          },
        }),
      )
    },
    onSuccess: props.onSaved,
    onError: () => {
      void status.refetch()
    },
  })
  let content = <s-text>{copy.statusLoading}</s-text>
  if (status.error)
    content = <s-banner tone="critical">{resolveMerchantErrorMessage(status.error, copy.statusFailed)}</s-banner>
  else if (status.data) {
    const data = status.data
    const rows = data.collection?.reportRowsStored ?? copy.unknownCount
    const reports = data.collection?.publishedReports ?? copy.unknownCount
    let failure = null
    if (data.latestFailure) failure = <s-banner tone="warning">{copy.reportFailed}</s-banner>
    content = (
      <s-stack gap="small">
        <s-text>{copy[collectionLabel(data)]}</s-text>
        <s-text>
          {copy.publishedReports}: {reports}
        </s-text>
        <s-text>
          {copy.storedRows}: {rows}
        </s-text>
        <s-text>
          {copy.latestPublished}: {data.latestPublishedDate ?? copy.unknownCount}
        </s-text>
        <s-text>
          {copy.activeRuns}: {data.collection?.activeRuns ?? copy.unknownCount}
        </s-text>
        <s-text>
          {copy.publicationPending}: {data.collection?.publicationPending ?? copy.unknownCount}
        </s-text>
        {failure}
      </s-stack>
    )
  }
  let confirmation = null
  if (confirming)
    confirmation = (
      <s-stack gap="small">
        <s-text>{copy.unlinkExplanation}</s-text>
        <s-button
          tone="critical"
          disabled={unlink.isPending}
          loading={unlink.isPending}
          onClick={() => unlink.mutate()}
        >
          {copy.confirmUnlink}
        </s-button>
      </s-stack>
    )
  let failure = null
  if (unlink.error)
    failure = <s-banner tone="critical">{resolveMerchantErrorMessage(unlink.error, copy.propertiesFailed)}</s-banner>
  let unlinkAction = null
  if (props.canUnlink)
    unlinkAction = (
      <s-button
        disabled={unlink.isPending}
        onClick={() => trackShopifyAction(props.shopify, 'confirm-unlink', () => setConfirming(true))}
      >
        {copy.unlink}
      </s-button>
    )
  return (
    <s-section heading={`${props.source.projectName}: ${props.source.property}`}>
      <s-stack gap="base">
        {content}
        {failure}
        <s-stack direction="inline" gap="base">
          <s-button
            disabled={status.isFetching}
            onClick={() => {
              started.current = Date.now()
              void trackShopifyRefetch(props.shopify, () => status.refetch())
            }}
          >
            {copy.refresh}
          </s-button>
          {unlinkAction}
          <s-button onClick={() => trackShopifyAction(props.shopify, 'close-provider', props.onClose)}>
            {copy.close}
          </s-button>
        </s-stack>
        {confirmation}
      </s-stack>
    </s-section>
  )
}
